import { useRef, useState } from 'react'
import { useDismiss } from '../lib/useDismiss'
import { ModeSwitch, type ModeTone } from './ModeSwitch'
import { useHubLang } from '../lib/i18n'
import { SHELL } from '../lib/hubCopy'
import { badgeText } from '../lib/waiting'

/**
 * One menu under your initials, top right: who you are, what else you may do,
 * and the way out. SHL-19.
 *
 * ── IT REPLACES OptionsMenu RATHER THAN SITTING BESIDE IT ───────────────────
 *
 * OptionsMenu is gone and this file carries its argument forward intact. That
 * component's reasoning was right and is not being reversed: the hub had three
 * mode pills in two places above the fold, a comms admin's hub did not look
 * like the hub, and the person signing off the layout was the one person who
 * could not see it. Every mode is still a <ModeSwitch>, still amber or red by
 * the same rule, still presentation and never a permission (rule 3).
 *
 * What changed is the arithmetic. Measured at 375px on 16 Sep: `.hubbar__tools`
 * holds 257px of controls in a 216px gap, so Ask One — the primary action —
 * ended at x=416 against a 375 viewport and was the whole of the page's
 * horizontal scroll. The "Options" button is 72px of that. Folded in here it
 * becomes part of a ~32px avatar that has to exist anyway, which closes the
 * 41px overflow without shrinking Ask One or hiding anything.
 *
 * ── THE TABS ARE WHERE YOU GO; THIS IS WHO YOU ARE AND WHAT YOU MAY DO ──────
 *
 * That seam is why the admin destinations are in here and not in the bottom
 * bar. Of 941 people, `tenant_admin` is 1, `stock_admin` 1, `stock_room_manager`
 * 4, `moderator` 5, `comms_admin` 6 — under ten people see any admin item at
 * all. A fourth tab spends a quarter of the bottom bar, for everybody, on a
 * destination almost nobody can open.
 *
 * It is NOT optional, though, and this is the part that would be easy to drop:
 * SHL-18 hides the rail below 820px, so without these links a moderator on a
 * phone cannot reach the queue at all. The links are the same items the rail
 * renders, gated the same way, and AppShell passes them already filtered — this
 * component must not learn what `admin.roles` is.
 *
 * SHL-33 (David, 10 Oct): the links are now ONE row, "Admin", opening the admin
 * home (#/admin), plus "Your questions". Eight rows for a comms admin who also
 * moderates is what pushed Sign out off a short window (SUP-1). The argument
 * above still holds — the row must exist below 820px — it is just one tap
 * further from each page, and that is the trade David chose.
 *
 * ── AN ARMED MODE IS STILL VISIBLE WITH THE MENU SHUT ───────────────────────
 *
 * Carried over unchanged, because it is the assertion that keeps hiding
 * moderator mode behind a menu honest: arming it puts an irreversible Remove on
 * every card. The trigger takes the tone of the armed mode and names what is
 * on. A menu that swallowed the state would be strictly worse than the row of
 * pills it replaced — and that is now doubly true, since this menu is also the
 * only way out of the mode.
 *
 * ── THE PROFILE ENTRY EXISTS NOW, AND IT IS NOT GATED ───────────────────────
 *
 * This file used to say there was no profile row because `#/profile` did not
 * exist and a link to a missing screen is SHL-8's trap. SHL-14 built the screen
 * — the signature and business card generators — so the row goes in, above the
 * links block, exactly where that note said it would.
 *
 * It is rendered UNCONDITIONALLY, unlike everything below it. The links block
 * is a filtered list of destinations under ten people can open; a profile is
 * something all 941 have, so gating it would be inventing a permission. That is
 * also why it is not a NAV item: the tabs are where you go, and this is who you
 * are — the same seam that keeps Administration out of the bottom bar.
 *
 * ── IT IS A MENU, NOT A DIALOG ──────────────────────────────────────────────
 *
 * Same `useDismiss` as the bell and the pickers: pointerdown so a press
 * starting outside closes on the way down, Escape returning focus to the
 * trigger, no focus trap, and the stack so the innermost open surface takes the
 * key. `role="group"`, not `role="dialog"` — 046 downgraded the bell for
 * exactly that reason and this must not reintroduce it.
 */
export type ModeOption = {
  /** Stable key. Not shown. */
  key: string
  /** The mode's name, e.g. "Moderator mode". */
  label: string
  /** What being in it actually does. Rendered as a line under the switch —
   *  not a `title`, because there is no hover on a tablet (046's reversal). */
  detail: string
  tone?: ModeTone
  on: boolean
  onToggle: (next: boolean) => void
}

/** A destination this person may open, already gated by AppShell. */
export type IdentityLink = {
  label: string
  /** Route without the leading slash, as NAV writes it. */
  route: string
  /** Rendered as a count beside the label (325: lib/waiting.ts). */
  badge?: number
}

export function IdentityMenu({
  userName,
  userInitials,
  userRole,
  modes = [],
  links = [],
  onNavigate,
  onSignOut,
  onReopenWelcome,
  onTakeTour,
  onOpenCard,
}: {
  userName: string
  userInitials: string
  userRole: string
  modes?: ModeOption[]
  links?: IdentityLink[]
  onNavigate: (route: string) => void
  onSignOut: () => void
  /**
   * Show the welcome modal again. David, 18 Sep: a tour you can only see once
   * is the wrong shape for a product people need explained twice.
   *
   * OPTIONAL, AND THE ROW RENDERS ONLY WHEN IT IS PASSED — which AppShell does
   * only when there is a welcome to reopen. SHL-8's trap is a menu item
   * pointing at something that is not there; here that would be a row that
   * opens nothing, on the screen whose whole job is telling you what you may
   * do. Every person in a window with a welcome running gets it, dismissed or
   * not; outside those windows nobody does.
   */
  onReopenWelcome?: () => void
  /**
   * SHL-34. Start the hub tour again (David, 10 Oct: "auto once, replay from
   * menu"). Optional for the welcome's reason — the row renders only when the
   * shell passes it — but the shell passes it on every page for everybody:
   * the tour, unlike a welcome, has no window it runs in.
   */
  onTakeTour?: () => void
  /**
   * Open the digital business card (David, 4 Oct). Optional for the welcome's
   * reason: AppShell passes it only while the card is released, and the row
   * renders only when it is passed — a row that opens nothing is SHL-8's trap.
   * Ungated otherwise, like the profile row: everybody has a card (or is told,
   * inside it, why the company cannot make one).
   */
  onOpenCard?: () => void
  // SHL-25's language picker LEFT this menu on 11 Oct (David) for #/profile,
  // with the languages you read as written (168). The menu is for going
  // places; Your profile is one row away on every screen.
}) {
  const t = SHELL[useHubLang()]
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useDismiss(wrapRef, open, (viaKeyboard) => {
    setOpen(false)
    if (viaKeyboard) buttonRef.current?.focus()
  })

  const armed = modes.filter((m) => m.on)
  // Red wins when anything dangerous is armed — the trigger has one slot and
  // the most consequential state is the one it owes the reader.
  const triggerTone = armed.some((m) => m.tone === 'danger') ? 'danger' : 'work'

  // 325. What waits behind this menu, summed onto the initials. Below 820px
  // the rail is gone and this menu is the only way to Admin, Your questions
  // and Surveys, so a count only inside the closed menu would be a count
  // nobody sees. The rows say which; the trigger says how many.
  const waiting = links.reduce((sum, l) => sum + (l.badge ?? 0), 0)

  // Unlike OptionsMenu, this never returns null. Everybody has a name and
  // everybody needs the way out, so there is no empty case — which is also why
  // sign-out could leave the rail: it now has a home on every screen and every
  // width, including the ones where the rail is gone.
  return (
    <div className="identitywrap" ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`identitybtn${armed.length ? ` identitybtn--on identitybtn--${triggerTone}` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        // The name is in the accessible label because the initials are not a
        // name: "DS" read aloud is two letters. The armed modes are NAMED
        // rather than counted — "2 on" says there is something to know and
        // not what it is.
        aria-label={
          (armed.length
            ? `${userName}. ${t.armedLabel(armed.map((m) => m.label).join(', '))}`
            : t.accountLabel(userName)) + (waiting > 0 ? `. ${t.waiting(waiting)}` : '')
        }
        title={armed.length ? t.armedLabel(armed.map((m) => m.label).join(', ')) : userName}
      >
        <span className="avatar avatar--onbrand" aria-hidden="true">
          {userInitials}
        </span>
        {armed.length > 0 && <span className="identitybtn__dot" aria-hidden="true" />}
        {waiting > 0 && (
          <span className="cornercount cornercount--below" aria-hidden="true">
            {badgeText(waiting)}
          </span>
        )}
      </button>

      {open && (
        <div className="identitypanel" role="group" aria-label={t.yourAccount}>
          <div className="identitypanel__head">
            <p className="identitypanel__name">{userName}</p>
            <p className="identitypanel__role">{userRole}</p>
          </div>

          <ul className="identitypanel__links">
            <li>
              <button
                type="button"
                className="identitypanel__link"
                onClick={() => {
                  onNavigate('profile')
                  setOpen(false)
                }}
              >
                <span>{t.nav['Your profile']}</span>
              </button>
            </li>
            {onOpenCard && (
              <li>
                <button
                  type="button"
                  className="identitypanel__link"
                  aria-haspopup="dialog"
                  onClick={() => {
                    // Close the menu FIRST: the dialog returns focus to
                    // whatever was focused when it opened, and that should be
                    // the avatar, not a row that is about to unmount.
                    setOpen(false)
                    buttonRef.current?.focus()
                    onOpenCard()
                  }}
                >
                  <span>{t.yourCard}</span>
                </button>
              </li>
            )}
            {/* UNGATED, like the profile above it and for the same reason: a
                welcome is something everybody in its window has, so gating it
                would be inventing a permission. It sits in this block rather
                than the rail because SHL-18 does not render the rail below
                820px at all — a rail entry would leave every phone user with no
                way back to it. */}
            {onReopenWelcome && (
              <li>
                <button
                  type="button"
                  className="identitypanel__link"
                  onClick={() => {
                    onReopenWelcome()
                    setOpen(false)
                  }}
                >
                  <span>{t.showWelcomeAgain}</span>
                </button>
              </li>
            )}
            {/* SHL-34. Ungated, beside the welcome and for its reason: the
                tour is everybody's, and the rail does not exist on a phone. */}
            {onTakeTour && (
              <li>
                <button
                  type="button"
                  className="identitypanel__link"
                  onClick={() => {
                    setOpen(false)
                    onTakeTour()
                  }}
                >
                  <span>{t.takeTour}</span>
                </button>
              </li>
            )}
            {/* 165. Ungated: everybody agreed to the terms of use to get here,
                and the help entries (terms_what, star_one_terms) send people
                to this row to read them again. */}
            <li>
              <button
                type="button"
                className="identitypanel__link"
                onClick={() => {
                  onNavigate('terms')
                  setOpen(false)
                }}
              >
                <span>{t.terms}</span>
              </button>
            </li>
          </ul>

          {links.length > 0 && (
            <ul className="identitypanel__links">
              {links.map((l) => (
                <li key={l.route}>
                  <button
                    type="button"
                    className="identitypanel__link"
                    onClick={() => {
                      onNavigate(l.route)
                      setOpen(false)
                    }}
                  >
                    <span>{l.label}</span>
                    {l.badge !== undefined && l.badge > 0 && (
                      <span className="navitem__badge" aria-label={t.waiting(l.badge)}>
                        {badgeText(l.badge)}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {modes.length > 0 && (
            <div className="identitypanel__modes">
              <div className="identitypanel__subhead">
                <h2>{t.otherRoles}</h2>
                {/* Says what this section is FOR, once, rather than repeating a
                    caveat on every row. Rule 3 in a sentence: these change what
                    this browser draws and nothing about what the database will
                    give anybody. */}
                <p>{t.otherRolesNote}</p>
              </div>
              <ul className="identitypanel__list">
                {modes.map((m) => (
                  <li key={m.key}>
                    <ModeSwitch
                      label={m.label}
                      tone={m.tone}
                      on={m.on}
                      onToggle={m.onToggle}
                    />
                    <p className="identitypanel__detail">{m.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="identitypanel__foot">
            <button type="button" className="btn btn--quiet" onClick={onSignOut}>
              {t.signOut}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
